import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Formik } from 'formik';
import { Alert, Badge, Button, Card, Col, Form } from 'react-bootstrap';
import ConfirmDialog from './components/ConfirmDialog';
import Loading from './components/Loading';
import useAdminData, { responseError } from './useAdminData';
import { adminFetch, AdminAuthError } from './adminAuth';
import { formatDateTime } from './format';
import { SALE_DATE_FIELDS, SALE_DATE_ORDER, SALE_DATE_LABELS } from './saleDates';
import { savedMessage } from './sitePublish';

const STATUS_VARIANTS = { open: 'success', closed: 'danger', maintenance: 'warning', unknown: 'secondary' };

const STATUS_CHOICES = [
  {
    status: 'open',
    label: 'Open consignment',
    variant: 'success',
    effect: 'Consigners will be able to sign up, sign in, and add or edit items.'
  },
  {
    status: 'closed',
    label: 'Close consignment',
    variant: 'danger',
    effect: "Consigners won't be able to sign in or change items. Admin pages keep working."
  },
  {
    status: 'maintenance',
    label: 'Maintenance',
    variant: 'warning',
    effect: 'Consigners will see a maintenance message instead of the consignment pages.'
  }
];

export const DATE_FIELDS = SALE_DATE_FIELDS;
const ORDER_RULES = SALE_DATE_ORDER;
const LABELS = SALE_DATE_LABELS;

/** API messages name fields ("saleStartDate must be before saleEndDate"); show the labels instead */
export function describeError(message) {
  return Object.entries(LABELS).reduce(
    (text, [name, label]) => text.split(name).join(label.toLowerCase()),
    message
  ).replace(/^./, first => first.toUpperCase());
}

/**
 * Form checks: every date set and in order, and the year matches the sale start.
 * Values are Eastern local "YYYY-MM-DDTHH:MM" strings, which compare correctly as text.
 */
export function validateDates(values) {
  const errors = {};
  DATE_FIELDS.forEach(({ name }) => {
    if (!values[name]) {
      errors[name] = 'Required';
    }
  });
  ORDER_RULES.forEach(([earlier, later, strict]) => {
    const a = values[earlier];
    const b = values[later];
    if (a && b && !errors[later] && (strict ? a >= b : a > b)) {
      errors[later] = `Must be ${strict ? 'after' : 'on or after'} "${LABELS[earlier].toLowerCase()}"`;
    }
  });
  const year = Number(values.currentYear);
  if (!Number.isInteger(year) || year < 2000 || year > 2100) {
    errors.currentYear = 'Enter a four-digit year';
  } else if (values.saleStartDate && Number(values.saleStartDate.slice(0, 4)) !== year) {
    errors.currentYear = `Must match the sale start (${values.saleStartDate.slice(0, 4)})`;
  }
  return errors;
}

/** An ended session goes back to sign in, like the other admin pages */
function useSignInAgain() {
  const history = useHistory();
  return err => {
    if (err instanceof AdminAuthError) {
      history.replace('/admin/login');
      return true;
    }
    return false;
  };
}

/** Readable reasons from a failed response, whatever shape its body has */
export async function failureReasons(response) {
  try {
    const body = await response.json();
    const reasons = (Array.isArray(body.errors) ? body.errors : [body.error]).filter(reason => typeof reason === 'string' && reason);
    if (reasons.length > 0) {
      return reasons.map(describeError);
    }
  } catch (err) {
    // Not JSON; fall through
  }
  return [`Request failed (${response.status})`];
}

function StatusCard({ status, onChanged }) {
  const signInAgain = useSignInAgain();
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const confirm = async () => {
    setBusy(true);
    try {
      const response = await adminFetch('/admin/config/status', { method: 'PUT', body: { status: pending.status } });
      if (!response.ok) {
        throw new Error(await responseError(response));
      }
      const result = await response.json();
      setMessage(savedMessage(result, `Consignment is now ${result.status}. ${result.note}`));
      onChanged();
    } catch (err) {
      if (signInAgain(err)) {
        return;
      }
      setMessage({ variant: 'danger', text: err.message });
    }
    setBusy(false);
    setPending(null);
  };

  return (
    <Card className="mb-3">
      <Card.Header>Consignment status</Card.Header>
      <Card.Body>
        <p>
          Currently{' '}
          <Badge variant={STATUS_VARIANTS[status] || 'secondary'} className="p-2">{status}</Badge>
        </p>
        {status === 'unknown' && (
          <Alert variant="danger">
            The status setting can't be read, so consigners are let in as if the sale were open. Setting a status below fixes it.
          </Alert>
        )}
        {message && <Alert variant={message.variant} onClose={() => setMessage(null)} dismissible>{message.text}</Alert>}
        <div className="d-flex flex-wrap">
          {STATUS_CHOICES.map(choice => (
            <Button
              key={choice.status}
              variant={choice.variant}
              className="mr-2 mb-2"
              disabled={choice.status === status}
              onClick={() => setPending(choice)}
            >
              {choice.label}
            </Button>
          ))}
        </div>
      </Card.Body>
      <ConfirmDialog
        show={!!pending}
        title={pending ? `${pending.label}?` : ''}
        message={pending && (
          <>
            <p>{pending.effect}</p>
            <p className="mb-0 text-muted small">The change reaches consigners within about a minute.</p>
          </>
        )}
        confirmLabel={pending ? pending.label : 'Confirm'}
        confirmVariant={pending ? pending.variant : 'primary'}
        busy={busy}
        onConfirm={confirm}
        onCancel={() => setPending(null)}
      />
    </Card>
  );
}

function DatesCard({ dates, datesLocal, onSaved }) {
  const signInAgain = useSignInAgain();
  const [message, setMessage] = useState(null);
  const [serverErrors, setServerErrors] = useState([]);
  const [yearChange, setYearChange] = useState(null);
  const [confirmingYear, setConfirmingYear] = useState(false);

  const initialValues = { currentYear: String(dates.currentYear || ''), ...datesLocal };

  const send = async (values, confirmYearChange = false) => {
    setMessage(null);
    setServerErrors([]);
    const body = { ...values, currentYear: Number(values.currentYear) };
    if (confirmYearChange) {
      body.confirmYearChange = true;
    }
    const response = await adminFetch('/admin/config/dates', { method: 'PUT', body });
    if (response.status === 409) {
      const result = await response.json();
      setYearChange({ ...result.yearChange, values });
      return;
    }
    if (!response.ok) {
      setServerErrors(await failureReasons(response));
      return;
    }
    const result = await response.json();
    setMessage(savedMessage(result, `Dates saved. ${result.note}`));
    onSaved();
  };

  const submit = async (values, { setSubmitting }) => {
    try {
      await send(values);
    } catch (err) {
      if (signInAgain(err)) {
        return;
      }
      setMessage({ variant: 'danger', text: err.message });
    }
    setSubmitting(false);
  };

  // The dialog stays open, busy, until the confirmed save finishes, so it can't be sent twice
  const confirmYear = async () => {
    const { values } = yearChange;
    setConfirmingYear(true);
    try {
      await send(values, true);
    } catch (err) {
      if (signInAgain(err)) {
        return;
      }
      setMessage({ variant: 'danger', text: err.message });
    }
    setConfirmingYear(false);
    setYearChange(null);
  };

  return (
    <Card>
      <Card.Header>Sale dates (Eastern time)</Card.Header>
      <Card.Body>
        {dates.isFallback && (
          <Alert variant="danger">The stored dates can't be read. Enter all of them and save to fix it.</Alert>
        )}
        {message && <Alert variant={message.variant} onClose={() => setMessage(null)} dismissible>{message.text}</Alert>}
        {serverErrors.length > 0 && (
          <Alert variant="danger">
            Not saved:
            <ul className="mb-0">{serverErrors.map(error => <li key={error}>{error}</li>)}</ul>
          </Alert>
        )}
        <Formik initialValues={initialValues} validate={validateDates} onSubmit={submit} enableReinitialize>
          {({ values, errors, handleChange, handleSubmit, isSubmitting, dirty }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <Form.Row>
                <Form.Group as={Col} sm={4} md={3} controlId="dates-currentYear">
                  <Form.Label>Sale year</Form.Label>
                  <Form.Control
                    name="currentYear"
                    inputMode="numeric"
                    value={values.currentYear}
                    onChange={handleChange}
                    isInvalid={!!errors.currentYear}
                  />
                  <Form.Control.Feedback type="invalid">{errors.currentYear}</Form.Control.Feedback>
                  {!errors.currentYear && (
                    <Form.Text muted>Item numbers look like AS{String(values.currentYear).slice(-2)}1001</Form.Text>
                  )}
                </Form.Group>
              </Form.Row>
              <Form.Row>
                {DATE_FIELDS.map(field => (
                  <Form.Group as={Col} md={6} key={field.name} controlId={`dates-${field.name}`}>
                    <Form.Label>{field.label}</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      step={(values[field.name] || '').length > 16 ? 1 : 60}
                      name={field.name}
                      value={values[field.name] || ''}
                      onChange={handleChange}
                      isInvalid={!!errors[field.name]}
                    />
                    <Form.Control.Feedback type="invalid">{errors[field.name]}</Form.Control.Feedback>
                    {!errors[field.name] && dates[field.name] && values[field.name] === datesLocal[field.name] && (
                      <Form.Text muted>{formatDateTime(dates[field.name])}</Form.Text>
                    )}
                  </Form.Group>
                ))}
              </Form.Row>
              <Button type="submit" disabled={isSubmitting || !dirty || Object.keys(errors).length > 0}>
                {isSubmitting ? 'Saving...' : 'Save dates'}
              </Button>
            </Form>
          )}
        </Formik>
      </Card.Body>
      <ConfirmDialog
        show={!!yearChange}
        title="Change the sale year?"
        message={yearChange && (
          <p className="mb-0">
            {yearChange.from === null
              ? `The current sale year can't be read, so it can't be compared. Saving sets it to ${yearChange.to}.`
              : `This changes the sale year from ${yearChange.from} to ${yearChange.to}.`}
            {' '}Every item number and Square SKU is built from it, so items already labeled or synced would be
            renumbered. Only change it when setting up a new sale.
          </p>
        )}
        confirmLabel={yearChange ? `Save with ${yearChange.to}` : 'Save'}
        busy={confirmingYear}
        onConfirm={confirmYear}
        onCancel={() => setYearChange(null)}
      />
    </Card>
  );
}

export default function SaleSettings() {
  const { data, error, loading, waking, reload } = useAdminData('/admin/config');

  if (loading && !data) {
    return <Loading waking={waking} />;
  }
  if (error) {
    return <Alert variant="danger">{error} <Button variant="link" onClick={reload}>Try again</Button></Alert>;
  }

  return (
    <div>
      <h2 className="mb-3">Sale settings</h2>
      <StatusCard status={data.status} onChanged={reload} />
      <DatesCard dates={data.dates} datesLocal={data.datesLocal} onSaved={reload} />
    </div>
  );
}
